import {
  LlmChatSessionDto,
  LlmChatMessageDto,
  LlmChatAttachmentDto,
  AgentConfigDto,
  LlmModelDto,
  AgentConfigPreviewSessionDto,
  AgentKnowledgeFileDto,
  CreateLlmChatSessionDto,
  CreateLlmChatMessageDto,
  UpdateLlmChatSessionModelDto,
  UpdateLlmChatSessionPinDto,
  type InquiryFieldAssistRequestDto,
  type InquiryFieldAssistResponseDto,
  type InquiryExpertiseSuggestRequestDto,
  type InquiryExpertiseSuggestResponseDto,
  type InquiryKpiSuggestRequestDto,
  type InquiryKpiSuggestResponseDto,
  type OfferScopeAssistRequestDto,
  type OfferScopeAssistResponseDto,
  type OfferMilestonesSuggestRequestDto,
  type OfferMilestonesSuggestResponseDto,
  type OfferRisksSuggestRequestDto,
  type OfferRisksSuggestResponseDto,
  type OfferCaseStudiesSuggestRequestDto,
  type OfferCaseStudiesSuggestResponseDto,
  type CreateUserAgentConfigDto,
  type UpdateUserAgentConfigDto,
  type UserAgentConfigDetailDto,
  type EntityMentionType,
  type MentionSearchResultDto,
  type LlmChatSlashCommandDto,
} from '@paladin-monorepo/common';
import API, { API_URL } from '../api';
import i18n from '@/i18n';
import type { LlmChatSessionsQueryFilters } from '../llm-chat-sessions-query';

export const llmChatApi = {
  async getAgentConfigs(): Promise<AgentConfigDto[]> {
    return API.get<AgentConfigDto[]>('/llm-chat/agent-configs').then((res) => res.data);
  },

  async getAgentConfig(id: string): Promise<UserAgentConfigDetailDto> {
    return API.get<UserAgentConfigDetailDto>(`/llm-chat/agent-configs/${id}`).then((res) => res.data);
  },

  async createAgentConfig(dto: CreateUserAgentConfigDto): Promise<AgentConfigDto> {
    return API.post<AgentConfigDto>('/llm-chat/agent-configs', dto).then((res) => res.data);
  },

  async updateAgentConfig(id: string, dto: UpdateUserAgentConfigDto): Promise<AgentConfigDto> {
    return API.patch<AgentConfigDto>(`/llm-chat/agent-configs/${id}`, dto).then((res) => res.data);
  },

  async deleteAgentConfig(id: string): Promise<void> {
    await API.delete(`/llm-chat/agent-configs/${id}`);
  },

  async createPreviewSession(configId: string): Promise<AgentConfigPreviewSessionDto> {
    return API.post<AgentConfigPreviewSessionDto>(
      `/llm-chat/agent-configs/${configId}/preview-session`,
    ).then((res) => res.data);
  },

  async listKnowledgeFiles(configId: string): Promise<AgentKnowledgeFileDto[]> {
    return API.get<AgentKnowledgeFileDto[]>(
      `/llm-chat/agent-configs/${configId}/knowledge-files`,
    ).then((res) => res.data);
  },

  async uploadKnowledgeFile(configId: string, file: File): Promise<AgentKnowledgeFileDto> {
    const formData = new FormData();
    formData.append('file', file);
    return API.post<AgentKnowledgeFileDto>(
      `/llm-chat/agent-configs/${configId}/knowledge-files`,
      formData,
    ).then((res) => res.data);
  },

  async deleteKnowledgeFile(configId: string, fileId: string): Promise<void> {
    await API.delete(`/llm-chat/agent-configs/${configId}/knowledge-files/${fileId}`);
  },

  async listAvailableModels(): Promise<LlmModelDto[]> {
    return API.get<LlmModelDto[]>('/llm-chat/available-models').then((res) => res.data);
  },

  async createSession(dto: CreateLlmChatSessionDto): Promise<LlmChatSessionDto> {
    return API.post<LlmChatSessionDto>('/llm-chat/sessions', dto, {
      params: { locale: i18n.language },
    }).then((res) => res.data);
  },

  async getSessions(filters?: LlmChatSessionsQueryFilters): Promise<LlmChatSessionDto[]> {
    const params: Record<string, string> = {};
    if (filters?.agentType) params.agentType = filters.agentType;
    if (filters?.inquiryId) params.inquiryId = filters.inquiryId;

    return API.get<LlmChatSessionDto[]>('/llm-chat/sessions', { params }).then((res) => res.data);
  },

  async getSession(id: string): Promise<{ session: LlmChatSessionDto; messages: LlmChatMessageDto[] }> {
    return API.get<{ session: LlmChatSessionDto; messages: LlmChatMessageDto[] }>(`/llm-chat/sessions/${id}`).then((res) => res.data);
  },

  async getSlashCommands(sessionId: string): Promise<LlmChatSlashCommandDto[]> {
    return API.get<LlmChatSlashCommandDto[]>(`/llm-chat/sessions/${sessionId}/slash-commands`).then(
      (res) => res.data,
    );
  },

  async deleteSession(sessionId: string): Promise<void> {
    await API.delete(`/llm-chat/sessions/${sessionId}`);
  },

  async updateSessionModel(
    sessionId: string,
    dto: UpdateLlmChatSessionModelDto,
  ): Promise<LlmChatSessionDto> {
    return API.patch<LlmChatSessionDto>(`/llm-chat/sessions/${sessionId}/model`, dto).then(
      (res) => res.data,
    );
  },

  async updateSessionPin(
    sessionId: string,
    dto: UpdateLlmChatSessionPinDto,
  ): Promise<LlmChatSessionDto> {
    return API.patch<LlmChatSessionDto>(`/llm-chat/sessions/${sessionId}/pin`, dto).then(
      (res) => res.data,
    );
  },

  async sendMessage(sessionId: string, dto: CreateLlmChatMessageDto): Promise<LlmChatMessageDto> {
    return API.post<LlmChatMessageDto>(`/llm-chat/sessions/${sessionId}/messages`, dto).then((res) => res.data);
  },

  async uploadAttachment(sessionId: string, file: File): Promise<LlmChatAttachmentDto> {
    const formData = new FormData();
    formData.append('file', file);
    return API.post<LlmChatAttachmentDto>(`/llm-chat/sessions/${sessionId}/attachments`, formData).then((res) => res.data);
  },

  async openOrDownloadAttachment(
    sessionId: string,
    attachmentId: string,
    filename: string,
    mimeType: string,
  ): Promise<void> {
    const response = await API.get(
      `/llm-chat/sessions/${encodeURIComponent(sessionId)}/attachments/${encodeURIComponent(attachmentId)}`,
      { responseType: 'blob' },
    );

    const blob = new Blob([response.data], { type: mimeType });
    const url = window.URL.createObjectURL(blob);

    const isViewable =
      mimeType.startsWith('image/') ||
      mimeType === 'application/pdf' ||
      mimeType.startsWith('text/') ||
      mimeType === 'application/json';

    if (isViewable) {
      window.open(url, '_blank', 'noopener,noreferrer');
      window.setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
      return;
    }

    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },

  async completeSession(sessionId: string, locale?: string): Promise<Record<string, unknown>> {
    const params = locale ? `?locale=${encodeURIComponent(locale)}` : '';
    return API.post<Record<string, unknown>>(`/llm-chat/sessions/${sessionId}/complete${params}`).then(
      (res) => res.data,
    );
  },

  async assistInquiryField(body: InquiryFieldAssistRequestDto): Promise<InquiryFieldAssistResponseDto> {
    return API.post<InquiryFieldAssistResponseDto>('/llm-chat/inquiry-field-assist', body).then((res) => res.data);
  },

  async suggestInquiryExpertise(
    body: InquiryExpertiseSuggestRequestDto,
  ): Promise<InquiryExpertiseSuggestResponseDto> {
    return API.post<InquiryExpertiseSuggestResponseDto>('/llm-chat/inquiry-expertise-suggest', body).then(
      (res) => res.data,
    );
  },

  async suggestInquiryKpis(body: InquiryKpiSuggestRequestDto): Promise<InquiryKpiSuggestResponseDto> {
    return API.post<InquiryKpiSuggestResponseDto>('/llm-chat/inquiry-kpi-suggest', body).then((res) => res.data);
  },

  async assistOfferScope(body: OfferScopeAssistRequestDto): Promise<OfferScopeAssistResponseDto> {
    return API.post<OfferScopeAssistResponseDto>('/llm-chat/offer-scope-assist', body).then((res) => res.data);
  },

  async suggestOfferMilestones(
    body: OfferMilestonesSuggestRequestDto,
  ): Promise<OfferMilestonesSuggestResponseDto> {
    return API.post<OfferMilestonesSuggestResponseDto>('/llm-chat/offer-milestones-suggest', body).then(
      (res) => res.data,
    );
  },

  async suggestOfferRisks(body: OfferRisksSuggestRequestDto): Promise<OfferRisksSuggestResponseDto> {
    return API.post<OfferRisksSuggestResponseDto>('/llm-chat/offer-risks-suggest', body).then((res) => res.data);
  },

  async suggestOfferCaseStudies(
    body: OfferCaseStudiesSuggestRequestDto,
  ): Promise<OfferCaseStudiesSuggestResponseDto> {
    return API.post<OfferCaseStudiesSuggestResponseDto>('/llm-chat/offer-case-studies-suggest', body).then(
      (res) => res.data,
    );
  },

  async getMentionTypes(): Promise<EntityMentionType[]> {
    return API.get<EntityMentionType[]>('/llm-chat/mentions/types').then((res) => res.data);
  },

  async searchMentions(params: {
    q: string;
    type?: EntityMentionType;
    limit?: number;
  }): Promise<MentionSearchResultDto> {
    return API.get<MentionSearchResultDto>('/llm-chat/mentions', { params }).then((res) => res.data);
  },

  getStreamUrl(sessionId: string, locale?: string): string {
    const params = locale ? `?locale=${encodeURIComponent(locale)}` : '';
    return `${API_URL}/llm-chat/sessions/${sessionId}/stream${params}`;
  },
};
